import type { ContentApiDatasource } from '../datasources/content-api.datasource';
import type {
  Paginated,
  Subject,
  SubjectView,
  SubjectViewDetail,
  SubjectViewMembership,
  SubjectEdge,
  ContentItem,
  ContentItemListEntry,
  ContentItemDetail,
  ExamFilterOptions,
  QuestionFilterOptions,
  QuestionSearchCard,
  SearchQuestionsInput,
  ContentSubjectLink,
  ContentLink,
  ListSubjectsInput,
  SubjectList,
  ListContentItemsInput,
  CreateSubjectInput,
  RenameSubjectInput,
  CreateSubjectViewInput,
  AddMembershipInput,
  AddSubjectEdgeInput,
  CreateContentItemInput,
  ClassifyContentInput,
  SetContentClassificationInput,
  LinkContentInput,
  CreateInstitutionInput,
  Institution,
  ListInstitutionsInput,
  UpdateInstitutionInput,
  MergeInstitutionsResult,
  CreateExamQuestionInput,
  QuestionImageInput,
  ExamQuestion,
  ExamPreview,
  ValidateExamImportInput,
  ValidateExamImportResult,
  BulkDeleteQuestionsResult,
  ContentEditLockResult,
  MoveExamQuestionsInput,
  MoveExamQuestionsResult,
  SetQuestionAnswerKeyInput,
  SetQuestionOfficialCommentInput,
  OfficialCommentWorkspace,
  ExamPreviewOfficialComment,
} from '../../domain/entities/content.entity';

/** Orquestra o datasource. Passthrough — sem lógica extra por ora. */
export class ContentRepositoryImpl {
  private api: ContentApiDatasource;

  constructor(api: ContentApiDatasource) {
    this.api = api;
  }

  // ---- Subjects ----------------------------------------------------------

  listSubjects(input?: ListSubjectsInput): Promise<SubjectList> {
    return this.api.listSubjects(input);
  }
  getSubject(subjectId: string): Promise<Subject> {
    return this.api.getSubject(subjectId);
  }
  createSubject(input: CreateSubjectInput): Promise<Subject> {
    return this.api.createSubject(input);
  }
  renameSubject(
    subjectId: string,
    input: RenameSubjectInput,
  ): Promise<Subject> {
    return this.api.renameSubject(subjectId, input);
  }
  deleteSubject(subjectId: string): Promise<Subject> {
    return this.api.deleteSubject(subjectId);
  }

  // ---- Subject views + memberships --------------------------------------

  listSubjectViews(): Promise<SubjectView[]> {
    return this.api.listSubjectViews();
  }
  getSubjectView(viewId: string): Promise<SubjectViewDetail> {
    return this.api.getSubjectView(viewId);
  }
  createSubjectView(input: CreateSubjectViewInput): Promise<SubjectView> {
    return this.api.createSubjectView(input);
  }
  addMembership(
    viewId: string,
    input: AddMembershipInput,
  ): Promise<SubjectViewMembership> {
    return this.api.addMembership(viewId, input);
  }
  removeMembership(viewId: string, subjectId: string): Promise<void> {
    return this.api.removeMembership(viewId, subjectId);
  }

  // ---- Subject edges -----------------------------------------------------

  listSubjectEdges(params?: {
    parentId?: string;
    childId?: string;
  }): Promise<SubjectEdge[]> {
    return this.api.listSubjectEdges(params);
  }
  addSubjectEdge(input: AddSubjectEdgeInput): Promise<SubjectEdge> {
    return this.api.addSubjectEdge(input);
  }
  removeSubjectEdge(parentId: string, childId: string): Promise<void> {
    return this.api.removeSubjectEdge(parentId, childId);
  }

  // ---- Content items -----------------------------------------------------

  listContentItems(
    input?: ListContentItemsInput,
  ): Promise<Paginated<ContentItemListEntry>> {
    return this.api.listContentItems(input);
  }
  getExamFilterOptions(
    institutionId: string,
    year?: number,
  ): Promise<ExamFilterOptions> {
    return this.api.getExamFilterOptions(institutionId, year);
  }
  searchQuestions(
    input: SearchQuestionsInput,
  ): Promise<Paginated<QuestionSearchCard>> {
    return this.api.searchQuestions(input);
  }
  getQuestionFilterOptions(
    parentSubjectId?: string,
  ): Promise<QuestionFilterOptions> {
    return this.api.getQuestionFilterOptions(parentSubjectId);
  }

  getContentItem(contentId: string): Promise<ContentItemDetail> {
    return this.api.getContentItem(contentId);
  }
  createContentItem(input: CreateContentItemInput): Promise<ContentItem> {
    return this.api.createContentItem(input);
  }
  updateContentItemBody(
    contentId: string,
    body: unknown,
    images?: QuestionImageInput[],
  ): Promise<ContentItem> {
    return this.api.updateContentItemBody(contentId, body, images);
  }
  publishContentItem(contentId: string): Promise<ContentItem> {
    return this.api.publishContentItem(contentId);
  }
  archiveContentItem(contentId: string): Promise<ContentItem> {
    return this.api.archiveContentItem(contentId);
  }
  deleteContentItem(contentId: string): Promise<ContentItem> {
    return this.api.deleteContentItem(contentId);
  }
  hardDeleteContentItem(contentId: string): Promise<void> {
    return this.api.hardDeleteContentItem(contentId);
  }

  // ---- Classificações + relações ----------------------------------------

  classifyContent(
    contentId: string,
    input: ClassifyContentInput,
  ): Promise<ContentSubjectLink> {
    return this.api.classifyContent(contentId, input);
  }
  setSubjectRelation(
    contentId: string,
    subjectId: string,
    relation: string,
  ): Promise<ContentSubjectLink> {
    return this.api.setSubjectRelation(contentId, subjectId, relation);
  }
  setQuestionOfficialComment(
    questionId: string,
    input: SetQuestionOfficialCommentInput,
  ): Promise<ExamPreviewOfficialComment | null> {
    return this.api.setQuestionOfficialComment(questionId, input);
  }
  getOfficialCommentWorkspace(
    questionId: string,
  ): Promise<OfficialCommentWorkspace> {
    return this.api.getOfficialCommentWorkspace(questionId);
  }
  unpublishOfficialComment(questionId: string): Promise<void> {
    return this.api.unpublishOfficialComment(questionId);
  }
  setContentClassification(
    contentId: string,
    input: SetContentClassificationInput,
  ): Promise<ContentSubjectLink[]> {
    return this.api.setContentClassification(contentId, input);
  }
  unclassifyContent(contentId: string, subjectId: string): Promise<void> {
    return this.api.unclassifyContent(contentId, subjectId);
  }
  linkContent(
    contentId: string,
    input: LinkContentInput,
  ): Promise<ContentLink> {
    return this.api.linkContent(contentId, input);
  }
  unlinkContent(
    contentId: string,
    targetContentId: string,
    relation: string,
  ): Promise<void> {
    return this.api.unlinkContent(contentId, targetContentId, relation);
  }

  // ---- Instituições ------------------------------------------------------

  listInstitutions(
    input?: ListInstitutionsInput,
  ): Promise<Paginated<Institution>> {
    return this.api.listInstitutions(input);
  }

  createInstitution(input: CreateInstitutionInput): Promise<Institution> {
    return this.api.createInstitution(input);
  }

  updateInstitution(
    institutionId: string,
    input: UpdateInstitutionInput,
  ): Promise<Institution> {
    return this.api.updateInstitution(institutionId, input);
  }

  archiveInstitution(institutionId: string): Promise<Institution> {
    return this.api.archiveInstitution(institutionId);
  }

  restoreInstitution(institutionId: string): Promise<Institution> {
    return this.api.restoreInstitution(institutionId);
  }

  mergeInstitutions(
    sourceInstitutionId: string,
    targetInstitutionId: string,
  ): Promise<MergeInstitutionsResult> {
    return this.api.mergeInstitutions(sourceInstitutionId, targetInstitutionId);
  }

  // ---- Provas: importação, preview e questões -----------------------------

  validateExamImport(
    input: ValidateExamImportInput,
  ): Promise<ValidateExamImportResult> {
    return this.api.validateExamImport(input);
  }
  getExamPreview(examId: string): Promise<ExamPreview> {
    return this.api.getExamPreview(examId);
  }
  createExamQuestion(
    examId: string,
    input: CreateExamQuestionInput,
  ): Promise<ExamQuestion> {
    return this.api.createExamQuestion(examId, input);
  }
  reorderExamQuestion(
    examId: string,
    questionId: string,
    newPosition: number,
  ): Promise<ContentLink[]> {
    return this.api.reorderExamQuestion(examId, questionId, newPosition);
  }
  removeExamQuestionReference(
    examId: string,
    questionId: string,
  ): Promise<void> {
    return this.api.removeExamQuestionReference(examId, questionId);
  }
  bulkDeleteQuestions(
    questionIds: string[],
  ): Promise<BulkDeleteQuestionsResult[]> {
    return this.api.bulkDeleteQuestions(questionIds);
  }
  setQuestionAnswerKey(
    questionId: string,
    input: SetQuestionAnswerKeyInput,
  ): Promise<ContentItem> {
    return this.api.setQuestionAnswerKey(questionId, input);
  }
  moveExamQuestions(
    input: MoveExamQuestionsInput,
  ): Promise<MoveExamQuestionsResult[]> {
    return this.api.moveExamQuestions(input);
  }
  acquireEditLocks(contentIds: string[]): Promise<ContentEditLockResult[]> {
    return this.api.acquireEditLocks(contentIds);
  }
  releaseEditLocks(contentIds: string[]): Promise<void> {
    return this.api.releaseEditLocks(contentIds);
  }
}
